import { useState } from "react";
import {
  Box, Container, Typography, Divider, Grid, List, ListItemButton,
  ListItemText, Chip, CircularProgress, Button, Pagination,
} from "@mui/material";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import {
  useGetPublishedPostsQuery, useGetPostsByCategoryQuery,
  useGetPublicCategoriesQuery, useGetWebsiteSettingsQuery,
} from "../../../../../app/websiteApiSlice";
import { useGetPublicSchoolInfoQuery } from "../../../../../app/authApiSlice";
import WebsiteHeader from "../../WebsiteHeader";
import WebsiteFooter from "../../WebsiteFooter";
import SeoHead from "../../SeoHead";
import "./classic.css";

export default function ClassicPostsList({ settings: settingsProp, categorySlug, page, onPageChange }) {
  const { data: school } = useGetPublicSchoolInfoQuery();
  const { data: settings } = useGetWebsiteSettingsQuery(undefined, { skip: !!settingsProp });
  const activeSettings = settingsProp || settings;
  const { data: categories = [] } = useGetPublicCategoriesQuery();

  const { data: allPosts, isLoading: loadingAll } = useGetPublishedPostsQuery({ page, per_page: 8 }, { skip: !!categorySlug });
  const { data: catData, isLoading: loadingCat } = useGetPostsByCategoryQuery({ slug: categorySlug, page, per_page: 8 }, { skip: !categorySlug });

  const isLoading = categorySlug ? loadingCat : loadingAll;
  const data = categorySlug ? catData : allPosts;
  const posts = data?.posts || [];
  const totalPages = data?.pages || 1;
  const activeCategory = categories.find((c) => c.slug === categorySlug);

  return (
    <Box className="website-classic" sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "#fdfaf5" }}>
      <SeoHead title={activeCategory ? `${activeCategory.name} — Blog` : "Blog"} schoolName={school?.name} />
      <WebsiteHeader school={school} navItems={activeSettings?.nav_items} variant="classic" />

      <Container maxWidth="lg" sx={{ py: 5, flex: 1 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Typography variant="h5" fontWeight={700} sx={{ fontFamily: "Georgia, serif", mb: 2 }}>
              {activeCategory ? activeCategory.name : "Latest Posts"}
            </Typography>
            <Divider sx={{ borderColor: "#795548", borderWidth: 2, mb: 3 }} />

            {isLoading ? (
              <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
            ) : posts.length === 0 ? (
              <Typography>No posts yet.</Typography>
            ) : (
              posts.map((post, i) => (
                <Box key={post.id} mb={4}>
                  {post.featured_image && (
                    <Box component="img" src={post.featured_image} alt={post.title}
                      sx={{ width: "100%", maxHeight: 220, objectFit: "cover", mb: 2, border: "3px solid #e0d6c8" }}
                    />
                  )}
                  <Typography variant="h6" fontWeight={700} component={Link} to={`/blog/${post.slug}`}
                    sx={{ textDecoration: "none", color: "#3e2723", fontFamily: "Georgia, serif", "&:hover": { textDecoration: "underline" } }}>
                    {post.title}
                  </Typography>
                  {post.published_at && (
                    <Typography variant="caption" display="block" color="text.secondary" mb={0.5}>
                      {format(new Date(post.published_at), "MMMM d, yyyy")}
                    </Typography>
                  )}
                  {post.excerpt && <Typography variant="body2" color="text.secondary">{post.excerpt}</Typography>}
                  {i < posts.length - 1 && <Divider sx={{ mt: 3 }} />}
                </Box>
              ))
            )}

            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination count={totalPages} page={page} onChange={(_e, v) => onPageChange(v)} />
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ bgcolor: "#fdf3e7", border: "1px solid #e0d6c8", borderRadius: 2, p: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: "Georgia, serif", mb: 1.5 }}>Categories</Typography>
              <Divider sx={{ mb: 1.5 }} />
              <List dense disablePadding>
                <ListItemButton component={Link} to="/blog" sx={{ borderRadius: 1 }}>
                  <ListItemText primary="All Posts" />
                </ListItemButton>
                {categories.map((c) => (
                  <ListItemButton key={c.id} component={Link} to={`/blog/category/${c.slug}`}
                    sx={{ borderRadius: 1, bgcolor: categorySlug === c.slug ? "#e8d5c4" : undefined }}>
                    <ListItemText primary={c.name} />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <WebsiteFooter settings={activeSettings} school={school} variant="classic" />
    </Box>
  );
}
